import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { API, useAuth } from "../App";
import DashboardLayout from "../components/DashboardLayout";
import OnboardingTour from "../components/OnboardingTour";
import ActivationCard from "../components/ActivationCard";
import AddCardBanner from "../components/AddCardBanner";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Lightning, FileText, Bookmark, Bell, ChartLineUp, ArrowRight, Clock, CheckCircle, XCircle, Compass, Sparkle, Timer, Warning, Ticket } from "@phosphor-icons/react";
import { toast } from "sonner";

const Dashboard = () => {
  const { user, refreshUser } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState({
    totalApplications: 0,
    bookmarks: 0,
    drafts: 0,
    approved: 0
  });
  const [recentApplications, setRecentApplications] = useState([]);
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newGrantCount, setNewGrantCount] = useState(0);
  const [discovering, setDiscovering] = useState(false);
  const [deadlineReminders, setDeadlineReminders] = useState([]);

  useEffect(() => {
    const fetchDashboardData = async () => {
      try {
        const [appsRes, bookmarksRes, notifsRes, newCountRes, remindersRes] = await Promise.all([
          axios.get(`${API}/applications`),
          axios.get(`${API}/bookmarks`),
          axios.get(`${API}/notifications`),
          axios.get(`${API}/grants/new-count`).catch(() => ({ data: { new_count: 0 } })),
          axios.get(`${API}/deadline-reminders`).catch(() => ({ data: { reminders: [] } }))
        ]);

        const apps = appsRes.data.applications || [];
        const bookmarks = bookmarksRes.data.bookmarks || [];
        const notifs = notifsRes.data.notifications || [];

        setStats({
          totalApplications: apps.length,
          bookmarks: bookmarks.length,
          drafts: apps.filter(a => a.status === "draft").length,
          approved: apps.filter(a => a.status === "approved").length
        });

        setRecentApplications(apps.slice(0, 5));
        setNotifications(notifs.slice(0, 5));
        setNewGrantCount(newCountRes.data.new_count || 0);
        setDeadlineReminders(remindersRes.data.reminders || []);
      } catch (error) {
        console.error("Error fetching dashboard data:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchDashboardData();
  }, []);

  // Auto-redeem pending promo code after login
  useEffect(() => {
    const pendingPromo = localStorage.getItem("pending_promo_code");
    if (pendingPromo && user && user.subscription_tier !== "pro") {
      const redeemPending = async () => {
        try {
          const res = await axios.post(`${API}/promo/redeem`, { code: pendingPromo });
          toast.success(res.data.message);
          localStorage.removeItem("pending_promo_code");
          if (refreshUser) refreshUser();
        } catch (error) {
          const detail = error.response?.data?.detail || "";
          if (detail.includes("already")) {
            localStorage.removeItem("pending_promo_code");
          }
          toast.error(detail || "Failed to redeem promo code");
        }
      };
      redeemPending();
    } else if (pendingPromo && user?.subscription_tier === "pro") {
      localStorage.removeItem("pending_promo_code");
    }
  }, [user]); // eslint-disable-line

  const handleDiscover = async () => {
    setDiscovering(true);
    try {
      const res = await axios.post(`${API}/grants/discover`, {});
      const count = res.data.count || 0;
      toast.success(`Discovered ${count} new grant${count !== 1 ? 's' : ''}!`);
      setNewGrantCount(prev => prev + count);
      // Navigate to grants sorted by newest
      navigate("/grants?sort=newest");
    } catch (error) {
      const detail = error.response?.data?.detail || "Failed to discover grants";
      toast.error(detail);
    } finally {
      setDiscovering(false);
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case "approved":
        return <CheckCircle weight="fill" className="w-4 h-4 text-emerald-400" />;
      case "rejected":
        return <XCircle weight="fill" className="w-4 h-4 text-red-400" />;
      case "submitted":
      case "under_review":
        return <Clock weight="fill" className="w-4 h-4 text-yellow-400" />;
      default:
        return <FileText weight="fill" className="w-4 h-4 text-zinc-400" />;
    }
  };

  const draftsRemaining = user?.subscription_tier === "pro" 
    ? "Unlimited" 
    : `${Math.max(0, 1 - (user?.drafts_used_this_month || 0))} / 1`;

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <div className="loading-spinner" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <OnboardingTour />
      <div className="space-y-8" data-testid="dashboard-page">
        {/* Welcome Section */}
        <div>
          <h1 className="text-3xl font-bold tracking-tight mb-2">
            Welcome back, {user?.name?.split(" ")[0] || "there"}
          </h1>
          <p className="text-zinc-400">
            {!user?.nonprofit_profile?.org_name ? (
              <span>
                Complete your <Link to="/profile" className="text-emerald-400 hover:underline">nonprofit profile</Link> to start applying for grants.
              </span>
            ) : (
              `Managing grants for ${user.nonprofit_profile.org_name}`
            )}
          </p>
        </div>

        {/* Promo Code Banner for Free Users */}
        {user?.subscription_tier !== "pro" && user?.role !== "admin" && (
          <Card className="bg-emerald-500/5 border-emerald-500/30 border-2 card-hover cursor-pointer" onClick={() => navigate("/pricing")} data-testid="promo-banner">
            <CardContent className="p-4">
              <div className="flex items-center gap-4">
                <div className="p-2.5 bg-emerald-500/15 rounded-sm">
                  <Ticket weight="duotone" className="w-6 h-6 text-emerald-400" />
                </div>
                <div className="flex-1">
                  <p className="font-bold text-white">Got a promo code? Unlock Premium for free!</p>
                  <p className="text-sm text-zinc-400">Redeem your code on the Pricing page to get full access</p>
                </div>
                <ArrowRight className="w-5 h-5 text-emerald-400 flex-shrink-0" />
              </div>
            </CardContent>
          </Card>
        )}

        {/* No-card trial: "Add a card to keep Premium" banner, fires only in final 5 days */}
        <AddCardBanner />

        {/* Profile Activation Nudge — renders only when profile < 60% complete */}
        <ActivationCard />

        {/* Discover New Grants */}
        <Card className="bg-gradient-to-r from-emerald-500/10 to-zinc-950 border-emerald-500/30 card-hover">
          <CardContent className="p-6">
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
              <div className="flex items-center gap-4">
                <div className="p-3 bg-emerald-500/20 rounded-sm">
                  <Compass weight="duotone" className="w-8 h-8 text-emerald-400" />
                </div>
                <div>
                  <h2 className="text-lg font-bold">New Verified Grants</h2>
                  <p className="text-sm text-zinc-400">
                    {newGrantCount > 0 
                      ? `${newGrantCount} new grants added recently!` 
                      : "Grants are added only after we check the funder's own website"}
                  </p>
                </div>
              </div>
              <div className="flex gap-3">
                {newGrantCount > 0 && (
                  <Button
                    onClick={() => navigate("/grants?sort=newest")}
                    variant="outline"
                    className="border-emerald-500/50 text-emerald-400 hover:bg-emerald-500/10"
                    data-testid="view-new-grants-btn"
                  >
                    View New ({newGrantCount})
                  </Button>
                )}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Stats Grid */}
        <div className="dashboard-grid">
          <Card className="bg-zinc-950 border-zinc-800 card-hover" data-testid="stat-applications">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-zinc-400 uppercase tracking-widest mb-1">Applications</p>
                  <p className="metric-value text-3xl">{stats.totalApplications}</p>
                </div>
                <FileText weight="duotone" className="w-10 h-10 text-emerald-500/30" />
              </div>
            </CardContent>
          </Card>

          <Card className="bg-zinc-950 border-zinc-800 card-hover" data-testid="stat-bookmarks">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-zinc-400 uppercase tracking-widest mb-1">Bookmarks</p>
                  <p className="metric-value text-3xl">{stats.bookmarks}</p>
                </div>
                <Bookmark weight="duotone" className="w-10 h-10 text-emerald-500/30" />
              </div>
            </CardContent>
          </Card>

          <Card className="bg-zinc-950 border-zinc-800 card-hover" data-testid="stat-drafts">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-zinc-400 uppercase tracking-widest mb-1">Flash Drafts</p>
                  <p className="metric-value text-3xl">{draftsRemaining}</p>
                </div>
                <Lightning weight="duotone" className="w-10 h-10 text-emerald-500/30" />
              </div>
            </CardContent>
          </Card>

          <Card className="bg-zinc-950 border-zinc-800 card-hover" data-testid="stat-approved">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-zinc-400 uppercase tracking-widest mb-1">Approved</p>
                  <p className="metric-value text-3xl">{stats.approved}</p>
                </div>
                <CheckCircle weight="duotone" className="w-10 h-10 text-emerald-500/30" />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Deadline Reminders */}
        {deadlineReminders.length > 0 && (
          <Card className="bg-zinc-950 border-zinc-800" data-testid="deadline-reminders-card">
            <CardHeader className="pb-4">
              <CardTitle className="text-lg flex items-center gap-2">
                <Timer weight="fill" className="w-5 h-5 text-orange-400" />
                Upcoming Deadlines
                <span className="text-xs bg-orange-500/20 text-orange-400 px-2 py-0.5 rounded-sm">{deadlineReminders.length}</span>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                {deadlineReminders.slice(0, 6).map((r) => (
                  <button
                    key={r.grant_id}
                    onClick={() => navigate(`/grants/${r.grant_id}`)}
                    className={`text-left p-3 rounded-sm border transition-colors hover:bg-zinc-900 ${
                      r.urgency === "critical" ? "border-red-500/40 bg-red-500/5" :
                      r.urgency === "warning" ? "border-orange-500/40 bg-orange-500/5" :
                      r.urgency === "upcoming" ? "border-yellow-500/40 bg-yellow-500/5" :
                      "border-zinc-800"
                    }`}
                    data-testid={`deadline-reminder-${r.grant_id}`}
                  >
                    <div className="flex items-center justify-between mb-1">
                      <span className={`text-xs font-bold px-1.5 py-0.5 rounded-sm ${
                        r.urgency === "critical" ? "bg-red-500/20 text-red-400" :
                        r.urgency === "warning" ? "bg-orange-500/20 text-orange-400" :
                        r.urgency === "upcoming" ? "bg-yellow-500/20 text-yellow-400" :
                        "bg-zinc-800 text-zinc-400"
                      }`}>
                        {r.days_left <= 1 ? "Tomorrow!" : `${r.days_left} days`}
                      </span>
                      {r.application_status === "draft" && (
                        <span className="text-xs text-amber-400 flex items-center gap-1">
                          <Warning className="w-3 h-3" />
                          Draft
                        </span>
                      )}
                    </div>
                    <p className="text-sm font-medium line-clamp-1">{r.title}</p>
                    <p className="text-xs text-zinc-500 mt-0.5">Up to ${r.amount_max?.toLocaleString()} &middot; {r.category}</p>
                  </button>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Quick Actions */}
        <div className="grid md:grid-cols-2 gap-6">
          <Card className="bg-zinc-950 border-zinc-800 card-hover">
            <CardHeader className="pb-4">
              <CardTitle className="text-lg flex items-center gap-2">
                <Lightning weight="fill" className="w-5 h-5 text-emerald-400" />
                Quick Actions
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <Button 
                onClick={() => navigate("/grants")}
                className="w-full justify-between bg-zinc-900 hover:bg-zinc-800 text-white border border-zinc-800"
                data-testid="browse-grants-action"
              >
                <span>Browse Available Grants</span>
                <ArrowRight className="w-4 h-4" />
              </Button>
              <Button 
                onClick={() => navigate("/applications")}
                className="w-full justify-between bg-zinc-900 hover:bg-zinc-800 text-white border border-zinc-800"
                data-testid="view-applications-action"
              >
                <span>View My Applications</span>
                <ArrowRight className="w-4 h-4" />
              </Button>
              {(user?.subscription_tier === "pro" || user?.role === "admin") && (
                <Button 
                  onClick={() => navigate("/post-grants")}
                  className="w-full justify-between bg-zinc-900 hover:bg-zinc-800 text-white border border-zinc-800"
                  data-testid="post-grant-action"
                >
                  <span>Post-Grant Manager</span>
                  <ChartLineUp className="w-4 h-4" />
                </Button>
              )}
            </CardContent>
          </Card>

          {/* Recent Notifications */}
          <Card className="bg-zinc-950 border-zinc-800 card-hover">
            <CardHeader className="pb-4">
              <CardTitle className="text-lg flex items-center gap-2">
                <Bell weight="fill" className="w-5 h-5 text-emerald-400" />
                Recent Notifications
              </CardTitle>
            </CardHeader>
            <CardContent>
              {notifications.length === 0 ? (
                <p className="text-zinc-500 text-sm">No notifications yet</p>
              ) : (
                <div className="space-y-3">
                  {notifications.map((notif) => (
                    <div 
                      key={notif.notification_id}
                      className={`p-3 rounded-sm border ${notif.read ? 'border-zinc-800 bg-zinc-900/50' : 'border-emerald-500/20 bg-emerald-500/5'}`}
                    >
                      <p className="text-sm font-medium">{notif.title}</p>
                      <p className="text-xs text-zinc-400 mt-1">{notif.message}</p>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Recent Applications */}
        <Card className="bg-zinc-950 border-zinc-800">
          <CardHeader className="pb-4">
            <div className="flex items-center justify-between">
              <CardTitle className="text-lg flex items-center gap-2">
                <FileText weight="fill" className="w-5 h-5 text-emerald-400" />
                Recent Applications
              </CardTitle>
              <Button 
                variant="ghost" 
                size="sm"
                onClick={() => navigate("/applications")}
                className="text-emerald-400 hover:text-emerald-300"
              >
                View All <ArrowRight className="w-4 h-4 ml-1" />
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            {recentApplications.length === 0 ? (
              <div className="text-center py-8">
                <FileText weight="duotone" className="w-12 h-12 text-zinc-700 mx-auto mb-3" />
                <p className="text-zinc-500 text-sm">No applications yet</p>
                <Button 
                  onClick={() => navigate("/grants")}
                  className="mt-4 bg-emerald-500 text-black font-bold hover:bg-emerald-400 rounded-sm"
                  data-testid="start-applying-btn"
                >
                  Start Applying
                </Button>
              </div>
            ) : (
              <div className="space-y-2">
                {recentApplications.map((app) => (
                  <div 
                    key={app.application_id}
                    onClick={() => navigate(`/grants/${app.grant_id}`)}
                    className="flex items-center justify-between p-4 rounded-sm border border-zinc-800 hover:border-zinc-700 cursor-pointer transition-colors"
                  >
                    <div className="flex items-center gap-3">
                      {getStatusIcon(app.status)}
                      <div>
                        <p className="font-medium">{app.grant_title}</p>
                        <p className="text-xs text-zinc-500">
                          Updated {new Date(app.updated_at).toLocaleDateString()}
                        </p>
                      </div>
                    </div>
                    <span className={`status-${app.status} px-2 py-1 rounded-sm text-xs font-mono uppercase`}>
                      {app.status.replace("_", " ")}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
};

export default Dashboard;
